import React from "react";

/**
 * A row of labelled figures — the shared "invoice strip" used by the
 * provisioning dialog's amount breakdown and by an expanded Purchase History
 * row, so the two read as the same object rather than two lookalikes.
 *
 * Separators come from `gap-px` over a divider-coloured background, not from
 * `divide-x`: these strips drop to two columns on mobile, and `divide-x` puts a
 * stray rule down the left edge of every wrapped row.
 */

// Static strings so Tailwind's scanner keeps them — a template literal built
// from `cols` would be purged out of the stylesheet.
const COLS = {
  2: "grid-cols-2",
  3: "grid-cols-2 sm:grid-cols-3",
  4: "grid-cols-2 sm:grid-cols-4",
  5: "grid-cols-2 sm:grid-cols-5",
};

export function FigureStrip({
  cols = 4,
  cellClass = "bg-card dark:bg-slate-900/80",
  className = "",
  children,
}) {
  return (
    <div
      className={`grid ${COLS[cols] || COLS[4]} gap-px overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700/70 bg-slate-200 dark:bg-slate-700/70 ${className}`}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child, {
              className: `${cellClass} ${child.props.className || ""}`,
            })
          : child
      )}
    </div>
  );
}

export function Figure({ icon: Icon, value, label, sub, valueClass = "", className = "" }) {
  return (
    <div className={`flex items-center gap-2.5 px-3 py-2.5 min-w-0 ${className}`}>
      {Icon && (
        <Icon className="h-5 w-5 shrink-0 text-slate-400 dark:text-slate-500" strokeWidth={1.75} />
      )}
      <div className="min-w-0">
        <p
          className={`text-sm font-semibold truncate ${valueClass || "text-slate-900 dark:text-foreground"}`}
          title={String(value)}
        >
          {value}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 truncate" title={label}>
          {label}
        </p>
        {sub}
      </div>
    </div>
  );
}
